import { api, ApiError } from '../api.js'

export const PTS2_CATEGORIES = Object.freeze([
  { key: 'PUMP', label: 'ПРК', tabLabel: 'ПРК', singular: 'ПРК' },
  { key: 'ATG', label: 'Рівнеміри', tabLabel: 'Рівнеміри', singular: 'Рівнемір ATG' },
  { key: 'PRICE_BOARD', label: 'Цінові табло', tabLabel: 'Цінове табло', singular: 'Цінове табло' },
  { key: 'READER', label: 'RFID-зчитувачі', tabLabel: 'RFID-зчитувачі', singular: 'RFID-зчитувач' },
  { key: 'SENSOR', label: 'Датчики', tabLabel: 'Датчики', singular: 'Датчик' },
].map(Object.freeze))

const categoryKeys = PTS2_CATEGORIES.map(({ key }) => key)
const deviceLimits = { PUMP: 100, ATG: 20, PRICE_BOARD: 5, READER: 100, SENSOR: 100 }
const connectionStates = ['FRESH', 'STALE', 'OFFLINE', 'UNKNOWN']
const evidenceStates = ['FRESH', 'STALE', 'HISTORICAL', 'UNKNOWN']
const configurationStates = ['CONFIRMED', 'ORDER_UNCERTAIN', 'HISTORICAL', 'UNKNOWN']
const deviceStates = ['IDLE', 'FILLING', 'END_OF_TRANSACTION', 'ONLINE', 'OFFLINE', 'ERROR', 'UNKNOWN']
const sorts = ['deviceId', 'protocol', 'state']
const directions = ['asc', 'desc']
const completenessStates = ['COMPLETE', 'PARTIAL', 'UNKNOWN']
const webSocketSchemes = ['WS', 'WSS']
const uploadTransports = ['WEBSOCKET', 'HTTP']
const transportSecurities = ['TLS', 'PLAIN']
const communicationModes = ['FULL_WEBSOCKET', 'UPLOAD_ONLY']
const connectionSchemes = ['HTTP', 'HTTPS', 'WS', 'WSS']
const controllerLifecycleStates = ['ACTIVE', 'INACTIVE', 'REVOKED']
const controllerConnectionStates = ['CONNECTED', 'STALE', 'OFFLINE', 'DISABLED']
const onboardingStages = ['CREDENTIALS_REQUIRED', 'SERVER_PARAMETERS', 'WAITING', 'VERIFYING', 'COMPLETE']
const onboardingErrors = ['HMAC_MISMATCH', 'INVALID_PROTOCOL_HEADER', 'INVALID_PACKET_SCHEMA', 'PROTOCOL_UNSUPPORTED']
const pts2CredentialUsername = /^(?!(?:PTS|Unipump|Dart[1-4]))[\x21-\x39\x3B-\x7E]{1,10}$/i
const pts2CredentialPassword = /^[\x21-\x7E]{1,10}$/
const pts2CredentialSecret = /^[\x21-\x7E]{1,20}$/
const labels = {
  IDLE: 'Очікує', FILLING: 'Наливає', END_OF_TRANSACTION: 'Транзакцію завершено',
  ONLINE: 'Онлайн', OFFLINE: 'Немає зв’язку', ERROR: 'Помилка', UNKNOWN: 'Стан невідомий',
}
const reasons = {
  CONFIGURATION_CONFLICT: 'Суперечливі дані конфігурації.',
  CONFIGURATION_INCOMPLETE: 'Конфігурація неповна.',
  CONFIGURATION_MISMATCH: 'Стан ще не зіставлено з цією конфігурацією.',
  STATUS_CONFLICT: 'Отримано суперечливі дані стану.',
  STATUS_INCOMPLETE: 'Дані стану неповні.',
  STATUS_MALFORMED: 'Формат отриманого стану не підтверджений.',
  STATUS_NOT_REPORTED: 'Контролер ще не повідомив стан цього пристрою.',
  STATUS_TIMESTAMP_MISSING: 'Час отримання стану невідомий.',
  STATUS_QUALITY_UNKNOWN: 'Якість даних стану не підтверджена.',
  STATUS_SCHEMA_UNAVAILABLE: 'Для цього пристрою немає підтверджених даних стану.',
  UNVERIFIED_ERROR_ENCODING: 'Повідомлення пристрою потребує уточнення.',
}

function isRecord(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function validInteger(value, minimum, maximum = Number.MAX_SAFE_INTEGER) {
  return Number.isSafeInteger(value) && value >= minimum && value <= maximum
}

function queryError() {
  return new ApiError('Некоректні параметри перегляду контролерів.', 400, 'pts2_query_invalid')
}

function responseError() {
  return new ApiError('Не вдалося перевірити дані контролера. Оновіть сторінку.', 502, 'pts2_response_invalid')
}

function positiveId(value) {
  if (typeof value === 'string' && /^[1-9][0-9]*$/.test(value)) value = Number(value)
  if (!validInteger(value, 1)) throw queryError()
  return value
}

function optionalQueryText(value, maximum) {
  if (value == null) return ''
  if (typeof value !== 'string' || value.length > maximum) throw queryError()
  return value
}

function queryChoice(value, allowed) {
  if (!allowed.includes(value)) throw queryError()
  return value
}

function pagination(cursor, limit) {
  if (!validInteger(limit, 1, 200)) throw queryError()
  return { cursor: optionalQueryText(cursor, 1024), limit }
}

function abortIfNeeded(signal) {
  if (signal?.aborted) throw new DOMException('Запит скасовано після зміни контексту.', 'AbortError')
}

function safeText(value, maximum = 512) {
  if (value == null) return null
  if (typeof value !== 'string' || value.length > maximum) throw responseError()
  return value
}

function safeNumber(value, minimum, maximum = Number.MAX_SAFE_INTEGER) {
  if (value == null) return null
  if (!validInteger(value, minimum, maximum)) throw responseError()
  return value
}

function safeChoice(value, allowed, fallback) {
  if (value == null) return fallback
  if (!allowed.includes(value)) throw responseError()
  return value
}

function readPage(payload, limit, project) {
  if (!isRecord(payload) || !Array.isArray(payload.items) || payload.items.length > limit
    || typeof payload.hasMore !== 'boolean') throw responseError()
  const nextCursor = safeText(payload.nextCursor, 1024)
  if (payload.hasMore && !nextCursor) throw responseError()
  const ids = new Set()
  const items = payload.items.map((value) => {
    if (!isRecord(value)) throw responseError()
    const item = project(value)
    const id = item.id ?? item.deviceId
    if (ids.has(id)) throw responseError()
    ids.add(id)
    return item
  })
  return { items, nextCursor, hasMore: payload.hasMore }
}

function projectCounts(value) {
  return Object.fromEntries(categoryKeys.map((category) => {
    const source = isRecord(value?.[category]) ? value[category] : {}
    const completeness = safeChoice(source.completeness, completenessStates, 'UNKNOWN')
    const observedCount = safeNumber(source.observedCount, 0, deviceLimits[category]) ?? 0
    const count = safeNumber(source.count, 0, deviceLimits[category])
    return [category, { count: completeness === 'COMPLETE' ? count : null, observedCount, completeness }]
  }))
}

function projectSummary(value, stationId) {
  if (!validInteger(value.id, 1) || value.stationId !== stationId) throw responseError()
  return {
    id: value.id, stationId,
    stableCode: safeText(value.stableCode, 80), displayName: safeText(value.displayName, 200),
    vendorPtsId: safeText(value.vendorPtsId, 24), firmwareDateTime: safeText(value.firmwareDateTime, 64),
    protocolRevision: safeNumber(value.protocolRevision, 0), configurationId: safeText(value.configurationId, 8),
    connectionFreshness: safeChoice(value.connectionFreshness, connectionStates, 'UNKNOWN'),
    lastSeenAt: safeText(value.lastSeenAt, 64), statusReceivedAt: safeText(value.statusReceivedAt, 64),
    statusFreshness: safeChoice(value.statusFreshness, evidenceStates, 'UNKNOWN'),
    configurationObservedAt: safeText(value.configurationObservedAt, 64),
    configurationState: safeChoice(value.configurationState, configurationStates, 'UNKNOWN'),
    configurationObservationId: safeText(value.configurationObservationId, 36),
    counts: projectCounts(value.counts),
  }
}

function projectBindings(value, category) {
  if (!isRecord(value)) throw responseError()
  if (category === 'READER') return {
    pumpId: safeNumber(value.pumpId, 0, 100),
    scope: safeChoice(value.scope, ['ALL_PUMPS', 'SINGLE_PUMP', 'UNKNOWN'], 'UNKNOWN'),
    nozzleNumber: safeNumber(value.nozzleNumber, 0, 6),
  }
  if (category === 'ATG') return {
    tankId: safeNumber(value.tankId, 1, 20),
    rule: safeChoice(value.rule, ['R140_TANK_PROBE_ID'], null),
  }
  if (category === 'PRICE_BOARD') {
    if (value.fuelGradeIds != null && (!Array.isArray(value.fuelGradeIds) || value.fuelGradeIds.length > 10)) throw responseError()
    return { fuelGradeIds: value.fuelGradeIds?.map((id) => safeNumber(id, 0, 20)) ?? null }
  }
  if (category === 'SENSOR') return {
    deviceType: safeChoice(value.deviceType, ['General', 'PTS', 'Pump', 'Probe', 'PriceBoard', 'Reader', 'Sensor'], null),
    deviceNumber: safeNumber(value.deviceNumber, 0, 100),
  }
  return {}
}

function projectDevice(value, category, configurationObservationId) {
  if (!validInteger(value.deviceId, 1, deviceLimits[category]) || value.category !== category
    || (value.protocolType != null && value.protocolType !== categoryKeys.indexOf(category))
    || (value.configurationObservationId ?? null) !== configurationObservationId) throw responseError()
  return {
    deviceId: value.deviceId, category,
    portId: category === 'PUMP' ? safeNumber(value.portId, 1, 4) : safeChoice(value.portId, ['DISP', 'LOG', 'USER', 'PC'], null),
    address: safeNumber(value.address, 1, 999999), protocolType: safeNumber(value.protocolType, 0, 4),
    protocolCode: safeNumber(value.protocolCode, 1, 99), protocolName: safeText(value.protocolName, 200),
    protocolNameSource: safeChoice(value.protocolNameSource, ['PO_MAP_2026_09_06', 'MISSING_CODE', 'UNKNOWN_CODE'], 'MISSING_CODE'),
    configurationObservationId: safeText(value.configurationObservationId, 36),
    bindings: projectBindings(value.bindings, category),
    state: safeChoice(value.state, deviceStates, 'UNKNOWN'),
    stateFreshness: safeChoice(value.stateFreshness, evidenceStates, 'UNKNOWN'),
    stateObservedAt: safeText(value.stateObservedAt, 64), stateReason: safeText(value.stateReason, 120),
  }
}

export async function getControllerSummaries(stationId, { cursor, limit = 200, signal } = {}) {
  stationId = positiveId(stationId)
  const paging = pagination(cursor, limit)
  const query = new URLSearchParams({ stationId: String(stationId), limit: String(paging.limit) })
  if (paging.cursor) query.set('cursor', paging.cursor)
  abortIfNeeded(signal)
  const payload = await api(`/pts2/controller-summaries?${query}`, { method: 'GET', signal })
  abortIfNeeded(signal)
  return readPage(payload, paging.limit, (item) => projectSummary(item, stationId))
}

function projectDiagnostics(value) {
  if (value == null) return null
  if (!isRecord(value)) throw responseError()
  const finite = (number) => {
    if (number == null) return null
    if (typeof number !== 'number' || !Number.isFinite(number)) throw responseError()
    return number
  }
  const receivedAt = safeText(value.receivedAt, 64)
  if (receivedAt !== null && (!/^\d{4}-\d{2}-\d{2}T.*Z$/.test(receivedAt) || !Number.isFinite(Date.parse(receivedAt)))) throw responseError()
  return {
    cpuTemperatureCelsius: finite(value.cpuTemperatureCelsius), batteryVoltage: finite(value.batteryVoltage),
    activeAlertCount: safeNumber(value.activeAlertCount, 0), receivedAt,
    freshness: safeChoice(value.freshness, evidenceStates, 'UNKNOWN'),
  }
}

function projectNetworkSummary(value) {
  if (!isRecord(value) || !validInteger(value.stationId, 1)) throw responseError()
  const stationDisplayName = safeText(value.stationDisplayName, 200)
  const stationStableCode = safeText(value.stationStableCode, 80)
  if (stationDisplayName === null || stationStableCode === null) throw responseError()
  return { ...projectSummary(value, value.stationId), stationDisplayName, stationStableCode,
    diagnostics: projectDiagnostics(value.diagnostics) }
}

function projectControllerSettings(value, stationId, controllerId) {
  if (!isRecord(value) || value.id !== controllerId || value.stationId !== stationId) throw responseError()
  const stableCode = safeText(value.stableCode, 80)
  const displayName = safeText(value.displayName, 120)
  const vendorPtsId = safeText(value.vendorPtsId, 24)
  const note = safeText(value.note, 2000)
  const version = safeNumber(value.version, 1)
  const status = safeChoice(value.status, controllerLifecycleStates, null)
  const webSocketScheme = safeChoice(value.webSocketScheme, webSocketSchemes, null)
  const communicationMode = safeChoice(value.communicationMode, communicationModes, 'FULL_WEBSOCKET')
  const inferredTransport = ['WS', 'WSS'].includes(value.connectionScheme) || communicationMode === 'FULL_WEBSOCKET'
    ? 'WEBSOCKET' : 'HTTP'
  const uploadTransport = safeChoice(value.uploadTransport, uploadTransports, inferredTransport)
  const connectionScheme = safeChoice(value.connectionScheme, connectionSchemes,
    uploadTransport === 'WEBSOCKET' ? webSocketScheme : webSocketScheme === 'WSS' ? 'HTTPS' : 'HTTP')
  const transportSecurity = safeChoice(value.transportSecurity, transportSecurities,
    webSocketScheme === 'WSS' ? 'TLS' : 'PLAIN')
  const expectedConnectionScheme = uploadTransport === 'WEBSOCKET'
    ? webSocketScheme : webSocketScheme === 'WSS' ? 'HTTPS' : 'HTTP'
  const observedTransport = value.observedTransport === null
    ? null
    : safeChoice(value.observedTransport, connectionSchemes, null)
  const connectionState = safeChoice(value.connectionState, controllerConnectionStates, null)
  const freshness = safeChoice(value.freshness, connectionStates, null)
  const lastSeenAt = safeText(value.lastSeenAt, 64)
  const credential = isRecord(value.credential) ? {
    configured: value.credential.configured === true,
    deviceUserId: safeNumber(value.credential.deviceUserId, 1, 30),
    deviceUsername: safeText(value.credential.deviceUsername, 10),
    version: safeNumber(value.credential.version, 0) ?? 0,
    activatedAt: safeText(value.credential.activatedAt, 64),
    revokedAt: safeText(value.credential.revokedAt, 64),
  } : null
  if (stableCode === null || displayName === null || vendorPtsId === null || note === null || version === null
    || status === null || uploadTransport === null || transportSecurity === null || webSocketScheme === null
    || value.observedTransport === undefined
    || connectionState === null || freshness === null || typeof value.credentialsConfigured !== 'boolean'
    || !/^[0-9A-F]{1,24}$/.test(vendorPtsId)
    || connectionScheme !== expectedConnectionScheme
    || (transportSecurity === 'TLS') !== (webSocketScheme === 'WSS')
    || (uploadTransport === 'HTTP' && communicationMode !== 'UPLOAD_ONLY')) throw responseError()
  return {
    id: controllerId,
    stationId,
    stableCode,
    displayName,
    vendorPtsId,
    status, uploadTransport, transportSecurity, communicationMode, connectionScheme,
    webSocketScheme,
    observedTransport,
    connectionState,
    freshness,
    lastSeenAt,
    credentialsConfigured: value.credentialsConfigured,
    credential,
    version,
    note,
  }
}

export async function getPlatformPts2Controllers(stationId, { signal } = {}) {
  stationId = positiveId(stationId)
  abortIfNeeded(signal)
  const payload = await api(`/platform/stations/${stationId}/pts2-controllers`, { method: 'GET', signal })
  abortIfNeeded(signal)
  if (!isRecord(payload) || !Array.isArray(payload.controllers) || payload.controllers.length > 200) throw responseError()
  return payload.controllers.map((value) => {
    if (!validInteger(value?.id, 1)) throw responseError()
    return projectControllerSettings(value, stationId, value.id)
  })
}

export async function createPlatformPts2Controller(stationId, value, { signal } = {}) {
  stationId = positiveId(stationId)
  const connection = normalizeConnectionSettings(value)
  abortIfNeeded(signal)
  const payload = await api(`/platform/stations/${stationId}/pts2-controllers`, {
    method: 'POST', signal, body: JSON.stringify({ ...value, ...connection }),
  })
  abortIfNeeded(signal)
  return projectControllerSettings(payload, stationId, payload?.id)
}

export async function updatePlatformPts2Controller(settings, { signal } = {}) {
  const connection = normalizeConnectionSettings(settings)
  const sourceStationId = positiveId(settings.sourceStationId ?? settings.stationId)
  const targetStationId = positiveId(settings.stationId)
  const controllerId = positiveId(settings.id)
  abortIfNeeded(signal)
  const payload = await api(`/platform/stations/${sourceStationId}/pts2-controllers/${controllerId}`, {
    method: 'PUT', signal,
    body: JSON.stringify({
      displayName: settings.displayName,
      stationId: targetStationId,
      ...connection,
      note: settings.note ?? '',
      version: settings.version,
    }),
  })
  abortIfNeeded(signal)
  return projectControllerSettings(payload, targetStationId, controllerId)
}

export async function updatePlatformPts2ControllerStatus(settings, status, { signal } = {}) {
  if (!isRecord(settings) || !controllerLifecycleStates.includes(status)) throw queryError()
  const stationId = positiveId(settings.stationId)
  const controllerId = positiveId(settings.id)
  abortIfNeeded(signal)
  const payload = await api(`/platform/stations/${stationId}/pts2-controllers/${controllerId}/status`, {
    method: 'PUT', signal, body: JSON.stringify({ status, version: settings.version }),
  })
  abortIfNeeded(signal)
  return projectControllerSettings(payload, stationId, controllerId)
}

export async function rotatePlatformPts2Credentials(settings, credentials, { signal } = {}) {
  if (!isRecord(settings) || !isRecord(credentials)) throw queryError()
  if (!validInteger(credentials.deviceUserId, 1, 30)
    || !pts2CredentialUsername.test(credentials.deviceUsername)
    || !pts2CredentialPassword.test(credentials.password)
    || !pts2CredentialSecret.test(credentials.hmacSecret)) throw queryError()
  const stationId = positiveId(settings.stationId)
  const controllerId = positiveId(settings.id)
  abortIfNeeded(signal)
  const payload = await api(`/platform/stations/${stationId}/pts2-controllers/${controllerId}/credentials`, {
    method: 'PUT', signal,
    body: JSON.stringify({ ...credentials, controllerVersion: settings.version }),
  })
  abortIfNeeded(signal)
  if (!isRecord(payload) || payload.configured !== true || !validInteger(payload.version, 1)) throw responseError()
  return {
    configured: true,
    deviceUserId: safeNumber(payload.deviceUserId, 1, 30),
    deviceUsername: safeText(payload.deviceUsername, 10),
    version: payload.version,
    activatedAt: safeText(payload.activatedAt, 64),
    revokedAt: safeText(payload.revokedAt, 64),
  }
}

function projectOnboarding(value, stationId, controllerId) {
  if (!isRecord(value) || value.stationId !== stationId || value.controllerId !== controllerId
    || typeof value.profileAvailable !== 'boolean') throw responseError()
  const controllerVersion = safeNumber(value.controllerVersion, 1)
  const stage = safeChoice(value.stage, onboardingStages, null)
  const profile = value.profile == null ? null : {
    uploadTransport: safeChoice(value.profile?.uploadTransport, uploadTransports, null),
    transportSecurity: safeChoice(value.profile?.transportSecurity, transportSecurities, null),
    communicationMode: safeChoice(value.profile?.communicationMode, communicationModes, null),
    connectionScheme: safeChoice(value.profile?.connectionScheme, connectionSchemes, null),
    scheme: safeChoice(value.profile?.scheme, webSocketSchemes, null),
    protocolType: safeChoice(value.profile?.protocolType, ['HTTP', 'HTTPS'], null),
    ipAddress: Array.isArray(value.profile?.ipAddress) && value.profile.ipAddress.length === 4
      && value.profile.ipAddress.every((item) => item === 0)
      ? [...value.profile.ipAddress] : null,
    domainName: safeText(value.profile?.domainName, 30),
    port: safeNumber(value.profile?.port, 1, 65535),
    uploadPumpTransactions: value.profile?.uploadPumpTransactions,
    uploadPumpTransactionsUri: safeText(value.profile?.uploadPumpTransactionsUri, 40),
    uploadTankMeasurements: value.profile?.uploadTankMeasurements,
    uploadTankMeasurementsUri: safeText(value.profile?.uploadTankMeasurementsUri, 40),
    uploadInTankDeliveries: value.profile?.uploadInTankDeliveries,
    uploadInTankDeliveriesUri: safeText(value.profile?.uploadInTankDeliveriesUri, 40),
    uploadGpsRecords: value.profile?.uploadGpsRecords,
    uploadGpsRecordsUri: safeText(value.profile?.uploadGpsRecordsUri, 40),
    uploadAlertRecords: value.profile?.uploadAlertRecords,
    uploadAlertRecordsUri: safeText(value.profile?.uploadAlertRecordsUri, 40),
    uploadPayments: value.profile?.uploadPayments,
    uploadPaymentsUri: safeText(value.profile?.uploadPaymentsUri, 40),
    uploadShifts: value.profile?.uploadShifts,
    uploadShiftsUri: safeText(value.profile?.uploadShiftsUri, 40),
    uploadConfiguration: value.profile?.uploadConfiguration,
    uploadConfigurationUri: safeText(value.profile?.uploadConfigurationUri, 40),
    uploadStatus: value.profile?.uploadStatus,
    uploadStatusUri: safeText(value.profile?.uploadStatusUri, 40),
    uploadStatusRequestsPeriodSeconds: safeNumber(
      value.profile?.uploadStatusRequestsPeriodSeconds, 1, 3600),
    requestTagsInformation: value.profile?.requestTagsInformation,
    requestTagsInformationUri: safeText(value.profile?.requestTagsInformationUri, 40),
    webSocketsUri: safeText(value.profile?.webSocketsUri, 40),
    webSocketsPort: safeNumber(value.profile?.webSocketsPort, 1, 65535),
    deviceUserId: safeNumber(value.profile?.deviceUserId, 1, 30),
    deviceUsername: safeText(value.profile?.deviceUsername, 10),
    useDeviceIdentifierAsLogin: value.profile?.useDeviceIdentifierAsLogin,
    useWebsocketsCommunication: value.profile?.useWebsocketsCommunication,
    serverResponseTimeoutSeconds: safeNumber(value.profile?.serverResponseTimeoutSeconds, 1, 65535),
    webSocketsUploadPumpTransactions: value.profile?.webSocketsUploadPumpTransactions,
    webSocketsUploadTankMeasurements: value.profile?.webSocketsUploadTankMeasurements,
    webSocketsUploadInTankDeliveries: value.profile?.webSocketsUploadInTankDeliveries,
    webSocketsUploadGpsRecords: value.profile?.webSocketsUploadGpsRecords,
    webSocketsUploadAlertRecords: value.profile?.webSocketsUploadAlertRecords,
    webSocketsUploadPayments: value.profile?.webSocketsUploadPayments,
    webSocketsUploadShifts: value.profile?.webSocketsUploadShifts,
    webSocketsUploadStatus: value.profile?.webSocketsUploadStatus,
    webSocketsUploadConfiguration: value.profile?.webSocketsUploadConfiguration,
    webSocketsRequestTagsInformation: value.profile?.webSocketsRequestTagsInformation,
    webSocketsUploadStatusRequestsPeriodSeconds: safeNumber(
      value.profile?.webSocketsUploadStatusRequestsPeriodSeconds, 1, 3600),
    webSocketsPingRequestsPeriodSeconds: safeNumber(
      value.profile?.webSocketsPingRequestsPeriodSeconds, 1, 3600),
    statusPeriodSeconds: safeNumber(value.profile?.statusPeriodSeconds, 1, 3600),
    pingPeriodSeconds: safeNumber(value.profile?.pingPeriodSeconds, 1, 3600),
    httpUploads: value.profile?.httpUploads,
    deviceCommands: value.profile?.deviceCommands,
  }
  if (profile && (profile.uploadTransport === null || profile.transportSecurity === null
    || profile.communicationMode === null || profile.connectionScheme === null
    || profile.scheme === null || profile.protocolType === null
    || profile.ipAddress === null || !profile.domainName || profile.port === null
    || !profile.uploadPumpTransactionsUri || !profile.uploadTankMeasurementsUri
    || !profile.uploadInTankDeliveriesUri || !profile.uploadGpsRecordsUri
    || !profile.uploadAlertRecordsUri || !profile.uploadPaymentsUri || !profile.uploadShiftsUri
    || !profile.uploadConfigurationUri || !profile.uploadStatusUri
    || profile.uploadStatusRequestsPeriodSeconds === null
    || !profile.requestTagsInformationUri
    || profile.webSocketsUri !== 'device/pts2/websocket'
    || profile.webSocketsPort === null || profile.deviceUserId === null
    || !profile.deviceUsername || profile.serverResponseTimeoutSeconds === null
    || profile.webSocketsUploadStatusRequestsPeriodSeconds === null
    || profile.webSocketsPingRequestsPeriodSeconds === null
    || profile.statusPeriodSeconds === null || profile.pingPeriodSeconds === null
    || [profile.uploadPumpTransactions, profile.uploadTankMeasurements,
      profile.uploadInTankDeliveries, profile.uploadGpsRecords, profile.uploadAlertRecords,
      profile.uploadPayments, profile.uploadShifts, profile.uploadConfiguration,
      profile.uploadStatus, profile.requestTagsInformation, profile.useDeviceIdentifierAsLogin,
      profile.useWebsocketsCommunication, profile.webSocketsUploadStatus,
      profile.webSocketsUploadConfiguration, profile.webSocketsUploadPumpTransactions,
      profile.webSocketsUploadTankMeasurements, profile.webSocketsUploadInTankDeliveries,
      profile.webSocketsUploadGpsRecords, profile.webSocketsUploadAlertRecords,
      profile.webSocketsUploadPayments, profile.webSocketsUploadShifts,
      profile.webSocketsRequestTagsInformation, profile.httpUploads,
      profile.deviceCommands].some((item) => typeof item !== 'boolean'))) throw responseError()
  if (profile) {
    const websocket = profile.uploadTransport === 'WEBSOCKET'
    const fullWebSocket = websocket && profile.communicationMode === 'FULL_WEBSOCKET'
    const secure = profile.connectionScheme === 'WSS' || profile.connectionScheme === 'HTTPS'
    const expectedScheme = secure ? 'WSS' : 'WS'
    const expectedProtocolType = secure ? 'HTTPS' : 'HTTP'
    const connectionModeMatches = websocket
      ? ['WS', 'WSS'].includes(profile.connectionScheme)
      : ['HTTP', 'HTTPS'].includes(profile.connectionScheme)
    const supportedUploads = [profile.webSocketsUploadPumpTransactions,
      profile.webSocketsUploadTankMeasurements, profile.webSocketsUploadInTankDeliveries,
      profile.webSocketsUploadAlertRecords, profile.webSocketsUploadStatus,
      profile.webSocketsUploadConfiguration]
    const supportedHttpUploads = [profile.uploadPumpTransactions, profile.uploadTankMeasurements,
      profile.uploadInTankDeliveries, profile.uploadAlertRecords, profile.uploadStatus]
    const httpUploadUris = [profile.uploadPumpTransactionsUri, profile.uploadTankMeasurementsUri,
      profile.uploadInTankDeliveriesUri, profile.uploadGpsRecordsUri, profile.uploadAlertRecordsUri,
      profile.uploadPaymentsUri, profile.uploadShiftsUri, profile.uploadConfigurationUri,
      profile.uploadStatusUri, profile.requestTagsInformationUri]
    if (!connectionModeMatches || (!websocket && profile.communicationMode !== 'UPLOAD_ONLY')
      || profile.transportSecurity !== (secure ? 'TLS' : 'PLAIN') || profile.scheme !== expectedScheme
      || profile.protocolType !== expectedProtocolType
      || profile.useWebsocketsCommunication !== fullWebSocket
      || profile.httpUploads !== !websocket
      || supportedUploads.some((item) => item !== websocket)
      || supportedHttpUploads.some((item) => item !== !websocket)
      || httpUploadUris.some((item) => item !== 'device/pts2')
      || profile.uploadStatusRequestsPeriodSeconds !== profile.statusPeriodSeconds
      || profile.webSocketsUploadStatusRequestsPeriodSeconds !== profile.statusPeriodSeconds
      || profile.webSocketsPingRequestsPeriodSeconds !== profile.pingPeriodSeconds
      || profile.uploadGpsRecords !== false || profile.uploadPayments !== false
      || profile.uploadShifts !== false || profile.uploadConfiguration !== false
      || profile.requestTagsInformation !== false
      || profile.useDeviceIdentifierAsLogin !== false || profile.deviceCommands !== false
      || profile.webSocketsUploadGpsRecords !== false || profile.webSocketsUploadPayments !== false
      || profile.webSocketsUploadShifts !== false || profile.webSocketsRequestTagsInformation !== false) {
      throw responseError()
    }
  }
  const source = value.evidence
  if (!isRecord(source) || typeof source.connected !== 'boolean' || typeof source.hmacVerified !== 'boolean') throw responseError()
  const evidence = {
    connected: source.connected,
    transport: source.transport == null ? null : safeChoice(source.transport, connectionSchemes, null),
    revisionQualification: safeChoice(source.revisionQualification,
      ['QUALIFIED', 'UNQUALIFIED', 'UNKNOWN'], source.protocolRevision == null ? 'UNKNOWN' : 'QUALIFIED'),
    connectedAt: safeText(source.connectedAt, 64),
    lastSeenAt: safeText(source.lastSeenAt, 64),
    deviceId: safeText(source.deviceId, 24),
    model: safeText(source.model, 32),
    firmwareDateTime: safeText(source.firmwareDateTime, 64),
    protocolRevision: safeNumber(source.protocolRevision, 0),
    configurationId: safeText(source.configurationId, 64),
    statusReceivedAt: safeText(source.statusReceivedAt, 64),
    configurationObservedAt: safeText(source.configurationObservedAt, 64),
    hmacVerified: source.hmacVerified,
  }
  const lastErrorCode = value.lastErrorCode == null ? null : safeChoice(value.lastErrorCode, onboardingErrors, null)
  if (controllerVersion === null || stage === null || value.profileAvailable !== (profile !== null)) throw responseError()
  return {
    controllerId, stationId, controllerVersion, stage,
    profileAvailable: value.profileAvailable, profile, evidence,
    configuredAt: safeText(value.configuredAt, 64),
    lastAttemptAt: safeText(value.lastAttemptAt, 64),
    lastErrorCode,
  }
}

export async function getPlatformPts2Onboarding(stationId, controllerId, { signal } = {}) {
  stationId = positiveId(stationId)
  controllerId = positiveId(controllerId)
  abortIfNeeded(signal)
  const payload = await api(`/platform/stations/${stationId}/pts2-controllers/${controllerId}/onboarding`, {
    method: 'GET', signal,
  })
  abortIfNeeded(signal)
  return projectOnboarding(payload, stationId, controllerId)
}

export async function confirmPlatformPts2Configuration(settings, { signal } = {}) {
  if (!isRecord(settings)) throw queryError()
  const stationId = positiveId(settings.stationId)
  const controllerId = positiveId(settings.id)
  abortIfNeeded(signal)
  const payload = await api(`/platform/stations/${stationId}/pts2-controllers/${controllerId}/onboarding`, {
    method: 'PUT', signal,
    body: JSON.stringify({ configured: true, controllerVersion: settings.version }),
  })
  abortIfNeeded(signal)
  return projectOnboarding(payload, stationId, controllerId)
}

export function generatePts2Secret(length = 20) {
  if (!validInteger(length, 1, 20) || !globalThis.crypto?.getRandomValues) throw queryError()
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789-_'
  const bytes = new Uint8Array(length)
  globalThis.crypto.getRandomValues(bytes)
  return Array.from(bytes, (value) => alphabet[value % alphabet.length]).join('')
}

export async function revokePlatformPts2Credentials(settings, { signal } = {}) {
  if (!isRecord(settings)) throw queryError()
  const stationId = positiveId(settings.stationId)
  const controllerId = positiveId(settings.id)
  abortIfNeeded(signal)
  await api(`/platform/stations/${stationId}/pts2-controllers/${controllerId}/credentials`, {
    method: 'DELETE', signal,
  })
  abortIfNeeded(signal)
}

export function controllerTransportState(settings) {
  const transport = settings?.uploadTransport ?? (settings?.communicationMode === 'UPLOAD_ONLY' ? 'HTTP' : 'WEBSOCKET')
  const configured = settings?.connectionScheme ?? (transport === 'WEBSOCKET'
    ? settings?.webSocketScheme : settings?.webSocketScheme === 'WSS' ? 'HTTPS' : 'HTTP')
  if (!connectionSchemes.includes(configured)
    || (settings?.observedTransport !== null && !connectionSchemes.includes(settings?.observedTransport))) throw responseError()
  const observed = settings.observedTransport
  return {
    configured,
    observed,
    secure: configured === 'WSS' || configured === 'HTTPS',
    warning: configured === 'WS' || configured === 'HTTP',
    mismatch: observed !== null && observed !== configured,
    observedKnown: observed !== null,
  }
}

export async function getNetworkControllerSummaries({ search = '', state = '', cursor, limit = 12, signal } = {}) {
  search = optionalQueryText(search, 120).trim()
  state = queryChoice(state, ['', ...connectionStates])
  const paging = pagination(cursor, limit)
  const query = new URLSearchParams()
  if (search) query.set('search', search)
  if (state) query.set('state', state)
  query.set('limit', String(paging.limit))
  if (paging.cursor) query.set('cursor', paging.cursor)
  abortIfNeeded(signal)
  const payload = await api(`/pts2/controllers/network-summaries?${query}`, { method: 'GET', signal })
  abortIfNeeded(signal)
  return readPage(payload, paging.limit, projectNetworkSummary)
}

export async function getControllerSummary(controllerId, { signal } = {}) {
  controllerId = positiveId(controllerId)
  abortIfNeeded(signal)
  const payload = await api(`/pts2/controllers/${controllerId}/summary`, { method: 'GET', signal })
  abortIfNeeded(signal)
  const summary = projectNetworkSummary(payload)
  if (summary.id !== controllerId) throw responseError()
  return summary
}

export async function getControllerSettings(stationId, controllerId, { signal } = {}) {
  stationId = positiveId(stationId)
  controllerId = positiveId(controllerId)
  abortIfNeeded(signal)
  const payload = await api(`/platform/stations/${stationId}/pts2-controllers/${controllerId}`, { method: 'GET', signal })
  abortIfNeeded(signal)
  return projectControllerSettings(payload, stationId, controllerId)
}

export async function updateControllerSettings(settings, webSocketScheme, { signal } = {}) {
  if (!isRecord(settings) || !webSocketSchemes.includes(webSocketScheme)) throw queryError()
  const stationId = positiveId(settings.stationId)
  const controllerId = positiveId(settings.id)
  const displayName = optionalQueryText(settings.displayName, 120)
  const note = optionalQueryText(settings.note, 2000)
  if (displayName.length < 2 || !validInteger(settings.version, 1)) throw queryError()
  abortIfNeeded(signal)
  const uploadTransport = settings.uploadTransport
    ?? (settings.communicationMode === 'UPLOAD_ONLY' ? 'HTTP' : 'WEBSOCKET')
  const connection = normalizeConnectionSettings({
    ...settings,
    uploadTransport,
    transportSecurity: webSocketScheme === 'WSS' ? 'TLS' : 'PLAIN',
    connectionScheme: uploadTransport === 'WEBSOCKET'
      ? webSocketScheme : webSocketScheme === 'WSS' ? 'HTTPS' : 'HTTP',
    webSocketScheme,
  })
  const payload = await api(`/platform/stations/${stationId}/pts2-controllers/${controllerId}`, {
    method: 'PUT',
    signal,
    body: JSON.stringify({ displayName, stationId, ...connection, note, version: settings.version }),
  })
  abortIfNeeded(signal)
  return projectControllerSettings(payload, stationId, controllerId)
}

function normalizeConnectionSettings(value) {
  if (!isRecord(value) || !webSocketSchemes.includes(value.webSocketScheme)) throw queryError()
  const communicationMode = value.communicationMode ?? 'FULL_WEBSOCKET'
  let uploadTransport = value.uploadTransport
  if (uploadTransport == null) {
    uploadTransport = ['WS', 'WSS'].includes(value.connectionScheme) || communicationMode === 'FULL_WEBSOCKET'
      ? 'WEBSOCKET' : 'HTTP'
  }
  const transportSecurity = value.transportSecurity
    ?? (value.webSocketScheme === 'WSS' ? 'TLS' : 'PLAIN')
  const connectionScheme = uploadTransport === 'WEBSOCKET'
    ? value.webSocketScheme : value.webSocketScheme === 'WSS' ? 'HTTPS' : 'HTTP'
  if (!uploadTransports.includes(uploadTransport) || !transportSecurities.includes(transportSecurity)
    || !communicationModes.includes(communicationMode)
    || (uploadTransport === 'HTTP' && communicationMode !== 'UPLOAD_ONLY')
    || (value.connectionScheme != null && value.connectionScheme !== connectionScheme)
    || (transportSecurity === 'TLS') !== (value.webSocketScheme === 'WSS')) throw queryError()
  return { uploadTransport, transportSecurity, communicationMode, connectionScheme,
    webSocketScheme: value.webSocketScheme }
}

export async function selectControllerContext(stationId, controllerId, { signal } = {}) {
  stationId = positiveId(stationId)
  controllerId = positiveId(controllerId)
  abortIfNeeded(signal)
  const payload = await api('/pts2/context/selection', { method: 'PUT', signal,
    body: JSON.stringify({ stationId, controllerId }) })
  abortIfNeeded(signal)
  if (!isRecord(payload?.platformContext) || !isRecord(payload?.pts2Context)
    || payload.platformContext.activeStationId !== stationId || payload.pts2Context.stationId !== stationId
    || payload.pts2Context.activeControllerId !== controllerId) throw responseError()
  return { platformContext: payload.platformContext, pts2Context: payload.pts2Context }
}

export async function getControllerDevices(stationId, controllerId, {
  category = 'PUMP', search = '', state = '', sort = 'deviceId', direction = 'asc', cursor, limit = 200, signal,
} = {}) {
  stationId = positiveId(stationId)
  controllerId = positiveId(controllerId)
  category = queryChoice(category, categoryKeys)
  search = optionalQueryText(search, 120).trim()
  state = queryChoice(state, ['', ...deviceStates])
  sort = queryChoice(sort, sorts)
  direction = queryChoice(direction, directions)
  const paging = pagination(cursor, limit)
  const query = new URLSearchParams({ stationId: String(stationId), category })
  if (search) query.set('search', search)
  if (state) query.set('state', state)
  query.set('sort', sort)
  query.set('direction', direction)
  if (paging.cursor) query.set('cursor', paging.cursor)
  query.set('limit', String(paging.limit))
  abortIfNeeded(signal)
  const payload = await api(`/pts2/controllers/${controllerId}/devices?${query}`, { method: 'GET', signal })
  abortIfNeeded(signal)
  if (!isRecord(payload) || payload.stationId !== stationId || payload.controllerId !== controllerId
    || payload.category !== category) throw responseError()
  const observationId = safeText(payload.configurationObservationId, 36)
  return {
    stationId, controllerId, category,
    configurationObservationId: observationId,
    configurationState: safeChoice(payload.configurationState, configurationStates, 'UNKNOWN'),
    connectionFreshness: safeChoice(payload.connectionFreshness, connectionStates, 'UNKNOWN'),
    statusReceivedAt: safeText(payload.statusReceivedAt, 64),
    statusFreshness: safeChoice(payload.statusFreshness, evidenceStates, 'UNKNOWN'),
    ...readPage(payload, paging.limit, (item) => projectDevice(item, category, observationId)),
  }
}

export function controllerCount(summary, category) {
  if (!categoryKeys.includes(category)) return null
  const value = summary?.counts?.[category]
  return value?.completeness === 'COMPLETE' && validInteger(value.count, 0, deviceLimits[category] ?? 0) ? value.count : null
}

function filterText(value) {
  return typeof value === 'string' ? value.trim().slice(0, 120) : ''
}

export function normalizeControllerFilters(value = {}) {
  return { search: filterText(value?.search), state: connectionStates.includes(value?.state) ? value.state : '' }
}

export function normalizeDeviceFilters(value = {}) {
  const category = categoryKeys.includes(value?.category) ? value.category : 'PUMP'
  return {
    category,
    search: filterText(value?.search), state: deviceFilterStates(category).includes(value?.state) ? value.state : '',
    sort: sorts.includes(value?.sort) ? value.sort : 'deviceId',
    direction: directions.includes(value?.direction) ? value.direction : 'asc',
  }
}

export function deviceFilterStates(category) {
  if (category === 'PUMP') return ['IDLE', 'FILLING', 'END_OF_TRANSACTION', 'OFFLINE', 'UNKNOWN']
  if (category === 'SENSOR') return ['UNKNOWN']
  return ['ONLINE', 'OFFLINE', 'ERROR', 'UNKNOWN']
}

export function deviceSearchQuery(value, deviceLabel = '') {
  const search = value.trim()
  let number = search
  if (deviceLabel && search.toLocaleLowerCase().startsWith(deviceLabel.toLocaleLowerCase())) {
    number = search.slice(deviceLabel.length).trim()
  }
  // Translate a displayed device number to the existing server search vocabulary.
  return /^(?:[№#]\s*)?\d+$/.test(number) ? number.replace(/^[№#]\s*/, '') : search
}

export function filterControllers(items, filters = {}) {
  const { search, state } = normalizeControllerFilters(filters)
  const needle = search.toLocaleLowerCase('uk')
  return (Array.isArray(items) ? items : []).filter((item) => {
    if (!isRecord(item)) return false
    const currentState = connectionStates.includes(item.connectionFreshness) ? item.connectionFreshness : 'UNKNOWN'
    const identity = [item.id, item.stableCode, item.displayName, item.vendorPtsId]
      .filter((value) => typeof value === 'string' || typeof value === 'number').join(' ').toLocaleLowerCase('uk')
    return (!state || state === currentState) && (!needle || identity.includes(needle))
  })
}

export function deviceStateView(row, { connectionFreshness, statusFreshness, failed = false } = {}, translate) {
  const state = deviceStates.includes(row?.state) ? row.state : 'UNKNOWN'
  const label = translate ? translate(`pts2UiDeviceState_${state}`) : labels[state]
  const text = (key, fallback) => translate ? translate(key) : fallback
  if (state === 'UNKNOWN') return { label, tone: 'unknown',
    detail: Object.hasOwn(reasons, row?.stateReason)
      ? text(`pts2UiReason_${row.stateReason}`, reasons[row.stateReason])
      : text('pts2UiStateUnconfirmed', 'Немає підтверджених даних стану.'), historical: false }
  const current = !failed && connectionFreshness === 'FRESH' && statusFreshness === 'FRESH' && row?.stateFreshness === 'FRESH'
  if (current) return { label, tone: ['OFFLINE', 'ERROR'].includes(state) ? 'offline' : 'fresh', detail: '', historical: false }
  return {
    label: `${label} · ${text('pts2UiLastState', 'останній стан')}`, tone: 'stale', historical: true,
    detail: failed ? text('pts2UiLastStateError', 'Останній отриманий стан. Не вдалося оновити дані.')
      : connectionFreshness === 'OFFLINE' ? text('pts2UiLastStateOffline', 'Останній отриманий стан. Контролер не на зв’язку.')
        : text('pts2UiLastStateUnconfirmed', 'Останній отриманий стан. Актуальність даних не підтверджена.'),
  }
}

export function deviceBindingLabel(row, translate) {
  const binding = row?.bindings
  const label = (key, fallback) => translate ? translate(key) : fallback
  if (!isRecord(binding)) return '—'
  if (row.category === 'READER') {
    if (binding.scope === 'ALL_PUMPS' && binding.pumpId === 0) return label('pts2UiAllPumps', 'Усі ПРК')
    if (binding.scope !== 'SINGLE_PUMP' || !validInteger(binding.pumpId, 1, 100)) return '—'
    const nozzle = validInteger(binding.nozzleNumber, 1, 6) ? ` · ${label('pts2UiNozzle', 'Пістолет')} №${binding.nozzleNumber}` : ''
    return `${label('pts2UiPump', 'ПРК')} №${binding.pumpId}${nozzle}`
  }
  if (row.category === 'ATG') return binding.rule === 'R140_TANK_PROBE_ID' && validInteger(binding.tankId, 1, 20)
    ? `${label('pts2UiTank', 'Резервуар')} №${binding.tankId}` : '—'
  if (row.category === 'PRICE_BOARD') return Array.isArray(binding.fuelGradeIds) && binding.fuelGradeIds.length > 0
    ? `${label('pts2UiFuelGrades', 'Марки палива')}: ${binding.fuelGradeIds.map((id) => validInteger(id, 1, 20) ? `№${id}` : '—').join(', ')}` : '—'
  if (row.category === 'SENSOR') {
    const names = { PTS: 'Контролер', Pump: 'ПРК', Probe: 'Рівнемір ATG', PriceBoard: 'Цінове табло', Reader: 'RFID-зчитувач', Sensor: 'Датчик' }
    return names[binding.deviceType] && validInteger(binding.deviceNumber, 1, 100)
      ? `${label(`pts2UiBinding_${binding.deviceType}`, names[binding.deviceType])} №${binding.deviceNumber}` : '—'
  }
  return '—'
}

export function createControllerViewMemory() {
  const entries = new Map()
  return {
    get(scope) {
      const value = entries.get(scope)
      return value ? { ...value } : null
    },
    set(scope, value) {
      if (typeof scope !== 'string' || !scope || scope.length > 512 || !isRecord(value)) return
      const selected = ['category', 'sort', 'direction'].some((key) => Object.hasOwn(value, key))
        ? normalizeDeviceFilters(value) : normalizeControllerFilters(value)
      entries.delete(scope)
      entries.set(scope, selected)
      if (entries.size > 64) entries.delete(entries.keys().next().value)
    },
    clear() { entries.clear() },
  }
}
